import {
    useInfiniteQuery,
    useQueryClient,
    InfiniteData,
    UseInfiniteQueryOptions,
    QueryFilters,
    InvalidateOptions,
    keepPreviousData
} from '@tanstack/react-query';

import {StringSearch} from '../../explore-view/hooks/requests/use-categories-query';
import {apiClient} from '../../../../utils/api-client';
import {CategoriesSearch, getCategoriesSearch} from '../../../../utils/get-categories-search';
import {
    addDocumentToCache,
    updateDocumentInCache,
    removeDocumentFromCache
} from '../../../../components/pagination/infinite-query-items';
import {DocumentStatus} from '../api';

import {getPageSize} from './categories/use-categories-infinite-query';
import {MetadataField} from '../../common/sidebar-search-field/interface';

const PAGE_SIZE = 200;

export interface MetadataFilter {
    fieldType: MetadataField;
    value: string;
}

interface SearchDocumentsRequest {
    types?: DocType[];
    statuses?: DocumentStatus[];
    stringSearch?: StringSearch;
    categoriesSearch?: CategoriesSearch;
    onlyUserDocuments?: boolean;
    limit: number;
    metadata?: MetadataFilter[];
    cursor: DocumentCursor | null;
}

export interface Props {
    queryKey: string;
    searchText?: string;
    statuses?: DocumentStatus[];
    categories?: string[];
    docType?: DocType;
    pageSize?: 'variable' | number;
    onlyUserDocuments?: boolean;
    metadata?: MetadataFilter[];
    options?: Pick<UseInfiniteQueryOptions<PageResponse<EditModeDoc>>, 'enabled'>;
}

function searchDocumentRequest(parameters: SearchDocumentsRequest) {
    return apiClient.post<PageResponse<EditModeDoc>>({
        path: 'editor-documents-service/api/documents/search',
        parameters
    });
}

export function useEditModeDocsInfiniteQuery({options = {}, pageSize = PAGE_SIZE, ...rest}: Props) {
    const key = [
        'searchEditMode',
        rest.queryKey,
        rest.docType,
        rest.categories,
        rest.searchText,
        rest.metadata,
        rest.statuses,
        rest.onlyUserDocuments,
        pageSize,
        useEditModeDocsInfiniteQuery.name
    ];

    return useInfiniteQuery({
        queryKey: key,
        queryFn: async ({pageParam}) => {
            return searchDocumentRequest({
                stringSearch: rest.searchText ? {strict: false, value: rest.searchText} : undefined,
                statuses: rest.statuses,
                categoriesSearch: getCategoriesSearch(rest.categories),
                onlyUserDocuments: rest.onlyUserDocuments,
                types: rest.docType ? [rest.docType] : undefined,
                cursor: pageParam.cursor,
                metadata: rest.metadata,
                limit: pageParam.limit
            });
        },
        getNextPageParam: ({nextCursor, remaining, total}) => {
            const hasNextPage = remaining > 0;

            if (hasNextPage) {
                return {
                    cursor: nextCursor,
                    limit: pageSize === 'variable' ? getPageSize(total - remaining) : pageSize
                };
            }
        },
        initialPageParam: {
            cursor: null,
            limit: pageSize === 'variable' ? getPageSize() : pageSize
        } as PageParams,
        placeholderData: keepPreviousData,
        enabled: Boolean(options?.enabled ?? true),
        notifyOnChangeProps: process.env.NODE_ENV === 'test' ? 'all' : undefined
    });
}

export function useEditModeDocsInfiniteQueryCache(baseQueryKey: string, addDocsToTheEnd?: boolean) {
    const queryClient = useQueryClient();
    const queryFilters: QueryFilters = {
        queryKey: ['searchEditMode', baseQueryKey],
        exact: false
    };

    function invalidate() {
        return queryClient.invalidateQueries({...queryFilters}, {
            refetchType: 'inactive'
        } as InvalidateOptions);
    }

    function addDocument(document: EditModeDoc) {
        queryClient.setQueriesData<InfiniteData<PageResponse<EditModeDoc>> | undefined>(
            queryFilters,
            (currentState) =>
                currentState && addDocumentToCache(currentState, document, addDocsToTheEnd)
        );
    }

    function updateDocument(updatedData: Partial<EditModeDoc> & {accession: string}) {
        queryClient.setQueriesData<InfiniteData<PageResponse<EditModeDoc>> | undefined>(
            queryFilters,
            (currentState) => currentState && updateDocumentInCache(currentState, updatedData)
        );
    }

    function deleteDocument(accession: string) {
        queryClient.setQueriesData<InfiniteData<PageResponse<EditModeDoc>> | undefined>(
            queryFilters,
            (currentState) => currentState && removeDocumentFromCache(currentState, accession)
        );
    }

    function clearCache() {
        queryClient.removeQueries(queryFilters);
    }

    return {invalidate, addDocument, updateDocument, deleteDocument, clearCache};
}
